import { useEffect, useMemo, useState } from "react";
import { api, type CalendarAccount, type CalendarEvent } from "../../lib/api";
import { useToast } from "../shell/Toast";

export function CalendarWorkspace() {
  const toast = useToast();
  const [calendars, setCalendars] = useState<CalendarAccount[]>([]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
  const [error, setError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [day, setDay] = useState("");
  const [calId, setCalId] = useState("");
  const [url, setUrl] = useState("");
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [name, setName] = useState("Personal");

  const range = useMemo(() => monthRange(cursor), [cursor]);

  async function refresh() {
    try {
      const c = await api.calendars();
      setCalendars(c.calendars);
      if (!calId && c.calendars[0]) setCalId(c.calendars[0].id);
      const e = await api.events(range.from.toISOString(), range.to.toISOString());
      setEvents(e.events);
      setError("");
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  useEffect(() => {
    void refresh();
  }, [cursor]);

  const cells = useMemo(() => buildMonthCells(cursor), [cursor]);

  async function addCalendar() {
    await api.addCalendar({
      name,
      url,
      username: user,
      password: pass,
      color: "#c4a574",
      provider: "caldav",
    });
    setUrl("");
    setPass("");
    toast.push({ message: "Calendar connected" });
    await refresh();
  }

  async function createEvent() {
    if (!calId || !day || !title) return;
    const start = new Date(`${day}T09:00:00`);
    const end = new Date(`${day}T10:00:00`);
    await api.saveEvent({
      calendarId: calId,
      title,
      start: start.toISOString(),
      end: end.toISOString(),
      allDay: false,
    });
    setFormOpen(false);
    setTitle("");
    toast.push({ message: "Event saved" });
    await refresh();
  }

  return (
    <div className="workspace" style={{ gridTemplateColumns: "260px 1fr" }}>
      <aside className="pane folders">
        <div className="pane-head">
          <div>
            <div className="eyebrow">Calendars</div>
            <strong>Schedule</strong>
          </div>
          <button className="btn btn-primary" onClick={() => setFormOpen(true)}>
            New
          </button>
        </div>
        <div style={{ padding: "0.75rem", display: "grid", gap: "0.55rem" }}>
          {calendars.map((c) => (
            <div key={c.id} style={{ display: "grid", gap: "0.25rem" }}>
              <button
                className={`folder-btn ${calId === c.id ? "active" : ""}`}
                onClick={() => setCalId(c.id)}
              >
                <span>
                  <span className="dot" style={{ background: c.color, marginRight: 8 }} />
                  {c.name}
                </span>
              </button>
              <button
                className="btn"
                style={{ margin: "0 0.55rem" }}
                onClick={async () => {
                  try {
                    const r = await api.syncCalendar(c.id);
                    toast.push({ message: `Synced ${r.imported} events` });
                    await refresh();
                  } catch (err) {
                    toast.push({ message: err instanceof Error ? err.message : String(err) });
                  }
                }}
              >
                Sync CalDAV
              </button>
            </div>
          ))}
          <div className="eyebrow">Add CalDAV</div>
          <input className="input" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
          <input className="input" placeholder="CalDAV URL" value={url} onChange={(e) => setUrl(e.target.value)} />
          <input className="input" placeholder="Username" value={user} onChange={(e) => setUser(e.target.value)} />
          <input
            className="input"
            type="password"
            placeholder="Password / token"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
          />
          <button className="btn" onClick={() => void addCalendar()}>
            Connect calendar
          </button>
          {error && <div className="error">{error}</div>}
        </div>
      </aside>
      <section className="pane read">
        <div className="pane-head">
          <div className="actions">
            <button className="btn" onClick={() => setCursor(addMonths(cursor, -1))}>
              ←
            </button>
            <strong>
              {cursor.toLocaleString(undefined, { month: "long", year: "numeric" })}
            </strong>
            <button className="btn" onClick={() => setCursor(addMonths(cursor, 1))}>
              →
            </button>
          </div>
          <button className="btn" onClick={() => setCursor(startOfMonth(new Date()))}>
            Today
          </button>
        </div>
        <div className="cal-body" style={{ padding: "0.75rem" }}>
          <div className="cal-grid" style={{ marginBottom: 1 }}>
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
              <div key={d} style={{ padding: "0.4rem", background: "var(--bg-2)", fontSize: "0.75rem" }}>
                {d}
              </div>
            ))}
          </div>
          <div className="cal-grid">
            {cells.map((cell) => {
              const dayEvents = events.filter((ev) => sameDay(new Date(ev.start), cell.date));
              return (
                <button
                  key={cell.date.toISOString()}
                  className={`cal-cell ${cell.inMonth ? "" : "muted"}`}
                  onClick={() => {
                    setDay(cell.date.toISOString().slice(0, 10));
                    setFormOpen(true);
                  }}
                >
                  <div>{cell.date.getDate()}</div>
                  {dayEvents.map((ev) => (
                    <span key={ev.id} className="cal-event">
                      {ev.title}
                    </span>
                  ))}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {formOpen && (
        <div className="modal-back" onClick={() => setFormOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0, fontFamily: "var(--font-display)" }}>Event</h3>
            <div className="form">
              <label className="label">Calendar</label>
              <select className="select" value={calId} onChange={(e) => setCalId(e.target.value)}>
                {calendars.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <label className="label">Title</label>
              <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
              <label className="label">Day</label>
              <input className="input" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
              <button className="btn btn-primary" onClick={() => void createEvent()}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function addMonths(d: Date, n: number) {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}
function monthRange(cursor: Date) {
  const from = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const to = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0, 23, 59, 59);
  return { from, to };
}
function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function buildMonthCells(cursor: Date) {
  const first = startOfMonth(cursor);
  const startPad = (first.getDay() + 6) % 7;
  const start = new Date(first);
  start.setDate(first.getDate() - startPad);
  return Array.from({ length: 42 }, (_, i) => {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    return { date, inMonth: date.getMonth() === cursor.getMonth() };
  });
}
